<script setup lang="ts">
import { ref, computed, inject, Ref, onMounted } from 'vue';
import { DateTime } from 'luxon';
import cloneDeep from 'lodash/cloneDeep';
import { useI18n } from 'vue-i18n';
import { useToast } from 'primevue/usetoast';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { STATUS_OPEN } from '@/constants';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useGatewayStore } from '@/store/GatewayStore';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import Button from 'primevue/button';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import SeasonalTemplate from '@/components/seasonal/SeasonalTemplate.vue';

const dialogRef = inject('dialogRef') as Ref;
const dataEdit = dialogRef?.value?.options?.data?.flightRule;
const closeModal = dialogRef?.value?.options?.emits?.closeModal;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const isLoading = ref(false);
const seasonalDefault = ref();
const gatewayValue = ref(dataEdit?.gateName ?? '');
const destinationValue = ref(dataEdit?.desName ?? '');
const filterGatewayResult = ref([]);
const filterDestinationResult = ref([]);
const overlapList = ref([]);
const isOverlapChecked = ref(false);
const instaquoteType = {
  FULL_RISK: 'FULL RISK',
  SHARED_RISK: 'SHARED RISK',
  FBR: 'FBR',
  PRE_REG: 'PRE REG'
};
const typeList = ref([instaquoteType.FULL_RISK, instaquoteType.SHARED_RISK, instaquoteType.FBR, instaquoteType.PRE_REG]);

const packageManagementStore = usePackageManagementStore();
const gatewayStore = useGatewayStore();

const destinationsList = computed(() => getListDestinationActive(packageManagementStore.destinations));
const gatewayList = computed(() => gatewayStore.selectGateways);

const { handleCheckError, checkEmptyPayload } = useComponentComposable();

const formatDurations = (durations: string[]) => {
  return durations ? durations.join(',') : '';
};

const formatDay = (value: string) => {
  const formatStringToNumber = parseInt(value);
  return isNaN(formatStringToNumber) ? value : formatStringToNumber.toString();
};

const formatDayOfWeek = (isSeasonalException?: boolean, value?: any) => {
  if (value) {
    return value;
  }
  return isSeasonalException ? [0, 0, 0, 0, 0, 0, 0] : [1, 1, 1, 1, 1, 1, 1];
};

const initSeasonalValue = (value?: any, isSeasonalException?: boolean | false) => {
  return value
    ? {
        ...value,
        durations: formatDurations(value.durations),
        startDay: formatDay(value.startDay),
        endDay: formatDay(value.endDay),
        dayOfWeeks: formatDayOfWeek(isSeasonalException, value.dayOfWeeks)
      }
    : { dayOfWeeks: formatDayOfWeek(isSeasonalException), durations: formatDurations([]), isDefaultSeason: true, startDay: '', startMonth: '', endDay: '', endMonth: '' };
};

const instaquoteRule = ref(
  dataEdit
    ? cloneDeep({
        ...dataEdit,
        seasonalException: {
          ...initSeasonalValue(dataEdit?.seasonalException, true),
          seasonType: 'EXCEPTION'
        },
        seasonalSummer: {
          ...initSeasonalValue(dataEdit?.seasonalSummer),
          seasonType: 'SUMMER'
        },
        seasonalWinter: {
          ...initSeasonalValue(dataEdit?.seasonalWinter),
          seasonType: 'WINTER'
        }
      })
    : {
        pk: '',
        sk: '',
        ruleType: '',
        gateCode: '',
        desCode: '',
        gateName: '',
        desName: '',
        minDuration: 0,
        maxDuration: 0,
        minPax: 0,
        estimatedArdTax: 0,
        minDay: 0,
        maxDay: 0,
        maxPax: 0,
        minFClass: 0,
        maxYClass: 0,
        minCost: 0,
        ruleStatus: STATUS_OPEN,
        seasonalException: {
          ...initSeasonalValue(undefined, true),
          seasonType: 'EXCEPTION'
        },
        seasonalSummer: {
          ...initSeasonalValue(),
          seasonType: 'SUMMER'
        },
        seasonalWinter: {
          ...initSeasonalValue(),
          seasonType: 'WINTER'
        }
      }
);

const regexDuration = helpers.regex(/^\d+(,\d{1,3})*$/);
const rules = {
  ruleType: { required },
  gateCode: { required },
  desCode: { required },
  minDuration: { required },
  maxDuration: { required },
  minPax: { required },
  maxPax: { required },
  minFClass: { required },
  maxYClass: { required },
  minDay: { required },
  maxDay: { required },
  seasonalException: {
    durations: { validateDuration: helpers.withMessage(t('validate.wong_format'), regexDuration) }
  },
  seasonalSummer: {
    durations: { validateDuration: helpers.withMessage(t('validate.wong_format'), regexDuration) }
  },
  seasonalWinter: {
    durations: { validateDuration: helpers.withMessage(t('validate.wong_format'), regexDuration) }
  }
};

const v$ = useVuelidate(rules, instaquoteRule.value);

const getListDestinationActive = (destinations: any) => {
  return destinations.filter((destination: any) => destination.status === STATUS_OPEN);
};

const searchGateway = async (event: AutoCompleteCompleteEvent) => {
  if (!event.query.trim().length) {
    filterGatewayResult.value = [...gatewayList.value];
  } else {
    filterGatewayResult.value = gatewayList.value.filter((air: any) => {
      return (
        air.code?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.airportName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.name?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.region?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        air.countryName?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const selectGateway = (e) => {
  if (e.value && typeof e.value === 'object') {
    instaquoteRule.value.gateCode = e.value.code;
    instaquoteRule.value.gateName = e.value.name;
    isOverlapChecked.value = false;
  }
};

const formatDestination = (listDestination) => {
  let dataList = cloneDeep(listDestination);
  const resultData = [];
  while (dataList.length > 0) {
    const country = dataList[0].country;
    resultData.push({
      label: country,
      items: dataList.filter((item) => item.country === country)
    });
    dataList = dataList.filter((item) => item.country !== country);
  }
  return resultData;
};

const searchDestination = async (event) => {
  if (!event.query.trim().length) {
    filterDestinationResult.value = formatDestination(destinationsList.value);
  } else {
    const filterResult = destinationsList.value.filter((des) => {
      return (
        des.airportCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        des.cityName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        des.country?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        des.name?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
    filterDestinationResult.value = formatDestination(filterResult);
  }
};

const selectDestination = (e) => {
  if (e.value && typeof e.value === 'object') {
    instaquoteRule.value.desCode = e.value.airportCode;
    instaquoteRule.value.desName = e.value.name;
    isOverlapChecked.value = false;
  }
};

const clearDestination = () => {
  destinationValue.value = null;
  instaquoteRule.value.desCode = '';
  instaquoteRule.value.desName = '';
  isOverlapChecked.value = false;
};

const clearGateway = () => {
  gatewayValue.value = null;
  instaquoteRule.value.gateCode = '';
  instaquoteRule.value.gateName = '';
  isOverlapChecked.value = false;
};

const formatDepartureDate = (date) => {
  if (!date) {
    return;
  }
  if (/\d{4}-\d{2}-\d{2}/.test(date)) {
    return date;
  }
  return DateTime.fromJSDate(new Date(date)).toFormat('yyyy-LL-dd');
};

const formatSeasonalUpload = (value: any) => {
  return {
    ...value,
    durations: value.durations ? value.durations?.split(',').map((item: string) => Number(item)) : []
  };
};

const renderDefault = (payload: any, defaultDate: any) => {
  return payload?.isDefaultSeason
    ? {
        ...payload,
        ...defaultDate
      }
    : {
        ...payload
      };
};

const submitRule = () => {
  const payload = { ...instaquoteRule.value };
  payload.minCost = payload.minCost ?? 0;
  payload.estimatedArdTax = payload.estimatedArdTax ?? 0;
  payload.depDateStart = formatDepartureDate(payload.depDateStart);
  payload.depDateEnd = formatDepartureDate(payload.depDateEnd);
  payload.seasonalException = formatSeasonalUpload(payload.seasonalException);
  payload.seasonalSummer = formatSeasonalUpload(renderDefault(payload.seasonalSummer, seasonalDefault.value?.summer));
  payload.seasonalWinter = formatSeasonalUpload(renderDefault(payload.seasonalWinter, seasonalDefault.value?.winter));
  if (!checkEmptyPayload(payload.seasonalSummer, payload.seasonalWinter, payload.seasonalException)) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.please_select_date'), life: 5000 });
  } else if (!handleCheckError(payload.seasonalSummer, payload.seasonalWinter)) {
    toast.add({ severity: 'error', summary: '', detail: t('management.package.unable_select_overlap_date') });
  } else {
    packageManagementStore.addInstaquoteRule(payload).then(() => {
      closeModal();
    });
  }
};

const submit = async () => {
  v$.value.$validate();
  if (!v$.value.$error) {
    submitRule();
  }
};

const handleGetError = (error: any) => {
  return {
    seasonalException: error?.seasonalException?.$errors?.[0]?.$message,
    seasonalSummer: error?.seasonalSummer?.$errors?.[0]?.$message,
    seasonalWinter: error?.seasonalWinter?.$errors?.[0]?.$message
  };
};

const changeType = async (event: any) => {
  isLoading.value = true;
  try {
    const seasonal = await packageManagementStore.getDetailSeasonalByType(event?.value);
    seasonalDefault.value = seasonal;
  } catch (error) {
    toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (dataEdit) {
    changeType({ value: dataEdit?.ruleType });
  }
});
</script>

<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="manage-instaquote-modal">
    <Fluid>
      <div class="field grid">
        <div class="col">
          <label for="type_flight">{{ $t('management.package.type') }}</label>
          <Select id="type_flight" v-model="instaquoteRule.ruleType" :options="typeList" @change="changeType($event)" class="w-full" :class="v$.ruleType.$error ? 'p-invalid' : ''" @update:modelValue="isOverlapChecked = false" />
          <p class="text-red-500 pt-1" v-if="v$.ruleType.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field">
        <label for="gateway">{{ $t('advance_quote.form.gateway') }}</label>
        <div class="flex align-items-center">
          <AutoComplete
            id="gateway"
            v-model="gatewayValue"
            :suggestions="filterGatewayResult"
            @complete="searchGateway($event)"
            :placeholder="$t('common.gateway')"
            optionLabel="airportName"
            optionValue="airportName"
            :completeOnFocus="true"
            @change="selectGateway($event)"
            class="w-full"
            forceSelection
            :class="v$.gateCode.$error ? 'p-invalid' : ''"
          >
            <template #option="slotProps">
              <div class="flex align-options-center">
                <div>
                  <div>{{ slotProps.option.code }} - {{ slotProps.option.airportName }}</div>
                  <div class="text-xs">{{ slotProps.option.name }}, {{ slotProps.option.region || slotProps.option.province }}, {{ slotProps.option.countryName }}</div>
                </div>
              </div>
            </template>
          </AutoComplete>
          <div>
            <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearGateway()"></i>
          </div>
        </div>
        <p class="text-red-500 pt-1" v-if="v$.gateCode.$error">{{ $t('validate.required') }}</p>
      </div>
    </Fluid>
    <Fluid>
      <div class="field">
        <label for="dest">{{ $t('advance_quote.form.destination') }}</label>
        <div class="flex align-items-center">
          <AutoComplete
            v-model="destinationValue"
            :suggestions="filterDestinationResult"
            @complete="searchDestination($event)"
            optionLabel="name"
            optionValue="name"
            :completeOnFocus="true"
            optionGroupLabel="label"
            optionGroupChildren="items"
            @change="selectDestination($event)"
            class="w-full"
            :class="v$.desCode.$error ? 'p-invalid' : ''"
            forceSelection
          >
            <template #option="slotProps">
              <div class="flex align-options-center">
                <div>
                  <div>{{ slotProps.option.airportCode }} - {{ slotProps.option.airportName }}</div>
                  <div class="text-xs">{{ slotProps.option.name }}, {{ slotProps.option.region }}, {{ slotProps.option.country }}</div>
                </div>
              </div>
            </template>
          </AutoComplete>
          <div>
            <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" @click="clearDestination()"></i>
          </div>
        </div>
        <p class="text-red-500 pt-1" v-if="v$.desCode.$error">{{ $t('validate.required') }}</p>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col">
          <label for="minDays"
            >{{ $t('management.package.min_days') }} <span class="small-text">({{ $t('management.package.departure_quote') }})</span></label
          >
          <InputNumber id="minDays" v-model="instaquoteRule.minDay" :class="v$.minDay.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.minDay.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col">
          <label for="maxDays"
            >{{ $t('management.package.max_days') }} <span class="small-text">({{ $t('management.package.departure_quote') }})</span></label
          >
          <InputNumber id="maxDays" v-model="instaquoteRule.maxDay" :class="v$.maxDay.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.maxDay.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col">
          <label for="min_duration">{{ $t('management.package.min_duration') }}</label>
          <InputNumber id="min_duration" v-model="instaquoteRule.minDuration" :class="v$.minDuration.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.minDuration.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col">
          <label for="max_duration">{{ $t('management.package.max_duration') }}</label>
          <InputNumber id="max_duration" v-model="instaquoteRule.maxDuration" :class="v$.maxDuration.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.maxDuration.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col">
          <label for="min_pax">{{ $t('management.package.min_pax') }}</label>
          <InputNumber id="min_pax" v-model="instaquoteRule.minPax" :class="v$.minPax.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.minPax.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col">
          <label for="max_pax">{{ $t('management.package.max_pax') }}</label>
          <InputNumber id="max_pax" v-model="instaquoteRule.maxPax" :class="v$.maxPax.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.maxPax.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col">
          <label for="min_f_class">{{ $t('management.package.min_f_class') }} (%)</label>
          <InputNumber id="min_f_class" v-model="instaquoteRule.minFClass" :class="v$.minFClass.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.minFClass.$error">{{ $t('validate.required') }}</p>
        </div>
        <div class="col">
          <label for="max_y_class">{{ $t('management.package.max_y_class') }} (%)</label>
          <InputNumber id="max_y_class" v-model="instaquoteRule.maxYClass" :class="v$.maxYClass.$error ? 'p-invalid' : ''" />
          <p class="text-red-500 pt-1" v-if="v$.maxYClass.$error">{{ $t('validate.required') }}</p>
        </div>
      </div>
    </Fluid>
    <Fluid>
      <div class="field grid">
        <div class="col-3">
          <label for="minCost">{{ $t('management.package.minimum_cost') }}</label>
          <InputNumber :placeholder="t('management.package.number_placeholder')" id="minCost" v-model="instaquoteRule.minCost" :minFractionDigits="2" :maxFractionDigits="2" :min="0" />
        </div>
        <div class="col-3">
          <label for="est_ard_taxes">{{ $t('management.package.est_ard_taxes') }}</label>
          <InputNumber :placeholder="t('management.package.number_placeholder')" id="est_ard_taxes" v-model="instaquoteRule.estimatedArdTax" :minFractionDigits="2" :maxFractionDigits="2" :min="0" />
        </div>
      </div>
    </Fluid>

    <div class="seasonal-arial">
      <SeasonalTemplate :seasonalDefault="seasonalDefault" :seasonalSummer="instaquoteRule.seasonalSummer" :seasonalWinter="instaquoteRule.seasonalWinter" :seasonalException="instaquoteRule.seasonalException" :validateSeason="handleGetError(v$)" />
    </div>
    <div class="flex justify-content-center mt-3">
      <Button :label="$t('voucher.submit')" @click="submit()" />
    </div>
    <div v-if="overlapList.length" class="border-2 p-2 border-red-500 border-round-md mt-3">
      <div class="text-center font-bold text-2xl text-900 mb-4">
        <span>{{ $t('management.package.alert') }}!</span>
      </div>
      <div class="text-center mb-4 text-700">
        <span>{{ $t('management.package.alert_message') }}!</span>
      </div>
      <div class="text-700">
        <div v-for="(overlap, overlapIndex) in overlapList" :key="'overlap' + overlapIndex">- ({{ overlap.gateName }} - {{ overlap.desName }}) - ({{ overlap.depDateStart }} - {{ overlap.depDateEnd }})</div>
      </div>
    </div>
  </div>
</template>
